/**
 * Modification command state machines — MOVE, COPY, MIRROR, ROTATE,
 * SCALE, OFFSET, STRETCH.
 *
 * Handles point input progression for each command and produces the
 * geometry reducer commands that apply the transformation.
 *
 * Extracted into a separate file to keep draftStateMachine.ts below
 * the 1000-line threshold.
 *
 * ## Function dependency map
 *
 * advanceModifySessionWithPoint(session, point, existingEntities, activeSpaceId)
 *   -> switch on session.step (move-*, copy-*, mirror-*, rotate-*,
 *      scale-*, offset-*, stretch-*)
 *   -> produces DraftCommitResult with geometry commands
 *
 * advanceModifySessionWithText(session, text, existingEntities, activeSpaceId)
 *   -> handles Enter (empty text) for COPY exit
 *   -> handles "Y"/"N" for MIRROR delete-source prompt (deferred)
 *   -> handles numeric input for ROTATE angle, SCALE factor, OFFSET distance
 *   -> falls through to point parsing for coordinate input
 *
 * isModifyStep(step) -> boolean
 *   -> quick discriminator for draftStateMachine to delegate here
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";
import { parsePointToken } from "@dxf-ai-editor/coordinate-input";
import type { DrawingEntity } from "@dxf-ai-editor/drawing-model";
import { findClosestHatchPick } from "@dxf-ai-editor/drawing-model";
import { arrayPathPlaneRefusal } from "@dxf-ai-editor/command-engine";
import { t } from "../i18n";
import {
  DEFAULT_ARRAY_CELL_HEIGHT,
  DEFAULT_ARRAY_CELL_WIDTH,
  isPathEntity,
} from "../state/arrayReducers";
// The ARRAYPATH frame refusal's user-facing line — the SAME projection the
// audit ring uses (`geometryShellReducer.ts`'s `geometry/create-arraypath`
// arm), so the command line explains a rejected path pick in the very words a
// CLI / AI dispatch of the same path would be answered with. One text, one
// truth; a leaf whose only other imports are `command-engine` and `../i18n`, so
// reaching it from `scene/` adds no cycle.
import { arrayPathPlaneRefusalAudit } from "../state/geometryShellHeavyPolylineAudit";
import type {
  DraftPoint,
  DraftSession,
  DraftStep,
  DraftCommitResult,
} from "./draftTypes";
import {
  referencePointForDraftTextInput,
  resolveEntityPreviewCoordinate,
} from "./draftSharedHelpers";
import {
  advanceGeomConstraintPick,
  commitGeomConstraintFromMore,
} from "./geomConstraintPicks";
import {
  interceptPointFilterPoint,
  interceptPointFilterText,
} from "./pointFilterSession";
import {
  interceptTemporaryTrackPointPoint,
  interceptTemporaryTrackPointText,
} from "./otrackTrackPointSession";

// ── Public discriminator ────────────────────────────────────────────

const MODIFY_STEPS: ReadonlySet<DraftStep> = new Set<DraftStep>([
  "move-select",
  "move-base-point",
  "move-destination",
  "copy-select",
  "copy-base-point",
  "copy-destination",
  "mirror-select",
  "mirror-first-point",
  "mirror-second-point",
  "rotate-select",
  "rotate-base-point",
  "rotate-angle",
  // Phase C.5 — ROTATE Reference sub-flow. Two reference clicks define
  // a vector whose current angle is captured; the user types the new
  // absolute angle, and the effective rotation = newAngle - refAngle.
  "rotate-ref-start",
  "rotate-ref-end",
  "rotate-ref-new-angle",
  "scale-select",
  "scale-base-point",
  "scale-factor",
  "offset-distance",
  "offset-select-entity",
  "offset-side",
  "stretch-select-first",
  "stretch-select-second",
  "stretch-base",
  "stretch-destination",
  // Batch 5
  "trim-select-edges",
  "trim-pick-entity",
  "extend-select-edges",
  "extend-pick-entity",
  "fillet-radius",
  "fillet-first-entity",
  "fillet-second-entity",
  "chamfer-distances",
  "chamfer-first",
  "chamfer-second",
  "break-select",
  "break-first-point",
  "break-second-point",
  "join-select",
  "explode-select",
  "lengthen-select",
  "lengthen-mode",
  "lengthen-value",
  "lengthen-pick-end",
  // ARRAYRECT modification steps (selection-first AutoCAD flow)
  "arrayrect-select",
  "arrayrect-base-point",
  "arrayrect-count",
  "arrayrect-spacing",
  // ARRAYPOLAR modification steps (selection-first AutoCAD flow)
  "arraypolar-select",
  "arraypolar-center",
  "arraypolar-count",
  "arraypolar-fill",
  // ARRAYPATH modification steps (selection-first AutoCAD flow)
  "arraypath-select",
  "arraypath-path",
  "arraypath-method",
  "arraypath-params",
  // ARRAYEDIT — user picks an array member; we then delegate to the
  // appropriate rect/polar/path flow with the arrayEditGroupId option
  // set so the final commit dispatches `geometry/arrayedit`.
  "arrayedit-select",
  // DYN-3 Batch E — geometric constraint multi-entity pick flow.
  "geom-constraint-pick-first",
  "geom-constraint-pick-second",
  "geom-constraint-pick-more",
  "geom-constraint-pick-reference-line",
]);

/**
 * Returns true if the given step belongs to a MOVE/COPY/MIRROR command
 * and should be handled by this module's advance functions.
 */
export function isModifyStep(step: DraftStep): boolean {
  return MODIFY_STEPS.has(step);
}

// ── Point input handler ─────────────────────────────────────────────

export function advanceModifySessionWithPoint(
  session: DraftSession,
  point: DraftPoint,
  existingEntities: EntityPreview[],
  activeSpaceId: string,
  // HATCHEDIT select hit-tests the clicked point against a HATCH's REAL
  // boundary geometry (preview-string coords don't exist on hatch previews).
  // The typed entities carry that geometry. Optional (defaults to []) so the
  // text-only / non-hatch callers and tests that don't thread them still work.
  geometryEntities: ReadonlyArray<DrawingEntity> = [],
): { nextSession: DraftSession | null; commit?: DraftCommitResult } {
  // activeSpaceId is passed through for downstream dispatch of entity-
  // creation commands (TRIM/EXTEND/FILLET/CHAMFER/etc.); keep the
  // binding live even in paths that don't reference it directly.
  void activeSpaceId;

  // OTRACK-SUBSYSTEM — `TT` interception ahead of the point-filter layer
  // (see `draftStateMachine.ts`'s identical wiring for the full rationale).
  // AutoCAD's TT works at ANY point prompt, including MOVE/COPY base +
  // destination.
  const ttPoint = interceptTemporaryTrackPointPoint(session, point);
  if (ttPoint) return ttPoint;

  // Point-filter / temporary-tracking interception (FROM base pick /
  // M2P picks / .X.Y.Z picks — ТЗ §8.6/§10.4) for MOVE/COPY/… base +
  // destination picks. Inactive sessions fall straight through.
  const filteredPoint = interceptPointFilterPoint(session, point, (s, p) =>
    advanceModifySessionWithPoint(
      s,
      p,
      existingEntities,
      activeSpaceId,
      geometryEntities,
    ),
  );
  if (filteredPoint) return filteredPoint;

  switch (session.step) {
    // ── MOVE ──────────────────────────────────────────────
    case "move-select": {
      // AutoCAD parity: clicking on an entity during the "Select objects"
      // prompt picks the entity and adds it to the selection set. The
      // step does NOT advance until the user presses Enter (which is
      // handled in advanceModifySessionWithText).
      const picked = findClosestEntity(point, existingEntities);
      if (!picked) return { nextSession: session };
      return {
        nextSession: appendTargetEntityId(session, picked.id),
      };
    }

    case "move-base-point":
      return {
        nextSession: {
          ...session,
          step: "move-destination",
          points: [point],
          hoverPoint: null,
        },
      };

    case "move-destination": {
      const basePoint = session.points[0];
      const dx = point.x - basePoint.x;
      const dy = point.y - basePoint.y;
      const entityIds = getTargetEntityIds(session);

      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/move-entities",
            source: "scene",
            entityIds,
            delta: { x: dx, y: dy },
          },
          summary: t("summary.move"),
          commandText: "MOVE",
        },
      };
    }

    // ── COPY ──────────────────────────────────────────────
    // AutoCAD prompt flow:
    //   1. "Specify base point or [Displacement]"
    //      - point click    → record base, advance to destination step
    //      - "D"/"Displacement" → flip displacementMode flag, re-prompt
    //        "Specify displacement"; next click is the single-vector
    //        displacement and the session commits + exits.
    //   2. "Specify second point or [Exit/Undo] <Exit>"
    //      - each click materialises ONE copy from base → clicked point
    //      - "E"/"Exit" or empty Enter ends the session
    //      - "U"/"Undo" would pop the last copy (TODO: needs an undo
    //        counter in session.options to route to shell/undo).
    //
    // TODO(copy-modifiers) — Array (A) and Mode (O) keywords are not
    // yet implemented. AutoCAD's Array mode creates a linear array of
    // the second-point pick; Mode toggles single / multi-copy.
    case "copy-select": {
      const picked = findClosestEntity(point, existingEntities);
      if (!picked) return { nextSession: session };
      return {
        nextSession: appendTargetEntityId(session, picked.id),
      };
    }

    case "copy-base-point":
      // Displacement mode: single click is the displacement vector
      // from the origin. Commit one copy and exit the session — matches
      // AutoCAD "Specify displacement" behaviour.
      if (session.options.copyDisplacementMode === "true") {
        const entityIdsDisp = getTargetEntityIds(session);
        if (entityIdsDisp.length === 0) return { nextSession: null };
        return {
          nextSession: null,
          commit: {
            geometryCommand: {
              type: "geometry/copy",
              source: "scene",
              sourceEntityIds: entityIdsDisp,
              basePoint: { x: 0, y: 0, z: 0 },
              secondPoint: { x: point.x, y: point.y, z: 0 },
            },
            summary: t("summary.copy_created", {
              n: entityIdsDisp.length,
            }),
            commandText: "COPY",
          },
        };
      }
      return {
        nextSession: {
          ...session,
          step: "copy-destination",
          points: [point],
          hoverPoint: null,
        },
      };

    case "copy-destination": {
      const basePoint = session.points[0];
      const entityIds = getTargetEntityIds(session);

      // AutoCAD: COPY loops — after placing a copy the user can keep
      // placing more copies from the same base point until pressing
      // Enter or Escape. We dispatch the AutoCAD-parity
      // `geometry/copy` command with explicit base + second so the
      // audit log + AI replay can reconstruct each iteration exactly.
      const committedCount =
        Number(session.options.copyCommittedCount ?? "0") + 1;
      return {
        nextSession: {
          ...session,
          step: "copy-destination",
          // Keep the base point intact for subsequent copies
          points: session.points,
          hoverPoint: null,
          options: {
            ...session.options,
            copyCommittedCount: String(committedCount),
          },
        },
        commit: {
          geometryCommand: {
            type: "geometry/copy",
            source: "scene",
            sourceEntityIds: entityIds,
            basePoint: { x: basePoint.x, y: basePoint.y, z: 0 },
            secondPoint: { x: point.x, y: point.y, z: 0 },
          },
          summary: t("summary.copy_created", { n: entityIds.length }),
          commandText: "COPY",
        },
      };
    }

    // ── MIRROR ────────────────────────────────────────────
    case "mirror-select": {
      const picked = findClosestEntity(point, existingEntities);
      if (!picked) return { nextSession: session };
      return {
        nextSession: appendTargetEntityId(session, picked.id),
      };
    }

    case "mirror-first-point":
      return {
        nextSession: {
          ...session,
          step: "mirror-second-point",
          points: [point],
          hoverPoint: null,
        },
      };

    case "mirror-second-point": {
      const line1 = session.points[0];
      const line2 = point;
      const entityIds = getTargetEntityIds(session);

      // AutoCAD prompts "Erase source objects? [Yes/No] <N>:" after
      // defining the mirror line. For now we default to No (keep source)
      // matching AutoCAD's default. A text-input step could be added
      // later for the Y/N prompt.
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/mirror-entities",
            source: "scene",
            entityIds,
            mirrorLine1: { x: line1.x, y: line1.y },
            mirrorLine2: { x: line2.x, y: line2.y },
            deleteSource: false,
          },
          summary: t("summary.mirror"),
          commandText: "MIRROR",
        },
      };
    }

    // ── ROTATE ─────────────────────────────────────────
    case "rotate-select": {
      const picked = findClosestEntity(point, existingEntities);
      if (!picked) return { nextSession: session };
      return {
        nextSession: appendTargetEntityId(session, picked.id),
      };
    }

    case "rotate-base-point":
      return {
        nextSession: {
          ...session,
          step: "rotate-angle",
          points: [point],
          hoverPoint: null,
        },
      };

    case "rotate-angle": {
      const basePoint = session.points[0];
      const angleDeg =
        (Math.atan2(point.y - basePoint.y, point.x - basePoint.x) * 180) /
        Math.PI;
      const entityIds = getTargetEntityIds(session);

      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/rotate-entities",
            source: "scene",
            entityIds,
            basePoint: { x: basePoint.x, y: basePoint.y },
            angleDeg,
          },
          summary: t("summary.rotate"),
          commandText: "ROTATE",
        },
      };
    }

    // ── ROTATE Reference sub-flow (Phase C.5) ─────────────────────
    case "rotate-ref-start": {
      // First reference-vector pick. Stash the point in `points[1]`
      // (points[0] is the rotation base point).
      return {
        nextSession: {
          ...session,
          step: "rotate-ref-end",
          points: [...session.points, point],
          hoverPoint: null,
        },
      };
    }
    case "rotate-ref-end": {
      // Second reference-vector pick. Compute the reference angle
      // from the (refStart → refEnd) vector and stash it in options
      // so the new-angle text step can subtract it.
      const refStart = session.points[1];
      const refAngleDeg =
        (Math.atan2(point.y - refStart.y, point.x - refStart.x) * 180) /
        Math.PI;
      return {
        nextSession: {
          ...session,
          step: "rotate-ref-new-angle",
          points: [...session.points, point],
          hoverPoint: null,
          options: {
            ...session.options,
            rotateRefAngle: String(refAngleDeg),
          },
        },
      };
    }
    case "rotate-ref-new-angle": {
      // Click at this step: the angle from the base point to the
      // clicked point becomes the new absolute angle.
      const baseRef = session.points[0];
      const newAngleDeg =
        (Math.atan2(point.y - baseRef.y, point.x - baseRef.x) * 180) / Math.PI;
      const refAngleDeg = parseFloat(session.options.rotateRefAngle ?? "0");
      const effectiveAngle = newAngleDeg - refAngleDeg;
      const entityIdsRef = getTargetEntityIds(session);
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/rotate-entities",
            source: "scene",
            entityIds: entityIdsRef,
            basePoint: { x: baseRef.x, y: baseRef.y },
            angleDeg: effectiveAngle,
          },
          summary: t("summary.rotate"),
          commandText: "ROTATE",
        },
      };
    }

    // ── SCALE ──────────────────────────────────────────
    case "scale-select": {
      const picked = findClosestEntity(point, existingEntities);
      if (!picked) return { nextSession: session };
      return {
        nextSession: appendTargetEntityId(session, picked.id),
      };
    }

    case "scale-base-point":
      return {
        nextSession: {
          ...session,
          step: "scale-factor",
          points: [point],
          hoverPoint: null,
        },
      };

    case "scale-factor": {
      const basePoint = session.points[0];
      const dist = Math.hypot(point.x - basePoint.x, point.y - basePoint.y);
      // Use the distance from base to the reference point as the scale
      // factor. If user clicks on the base point, default to 1.
      const factor = dist < 1e-10 ? 1 : dist;
      const entityIds = getTargetEntityIds(session);

      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/scale-entities",
            source: "scene",
            entityIds,
            basePoint: { x: basePoint.x, y: basePoint.y },
            factor,
          },
          summary: t("summary.scale"),
          commandText: "SCALE",
        },
      };
    }

    // ── OFFSET ─────────────────────────────────────────
    case "offset-distance":
      // Distance is entered via text input, not point click.
      return { nextSession: session };

    case "offset-select-entity": {
      // User clicks on an entity to offset. Find the closest entity
      // under the cursor. We store the entity ID in session.options.
      // Restricted to OFFSET's own reducer-supported types (Codex P2,
      // local review, batch-5 follow-up round 3) — see
      // `OFFSET_AND_ARRAYPATH_SUPPORTED_TYPES`'s own doc comment.
      const closest = findClosestEntity(
        point,
        existingEntities,
        true,
        OFFSET_AND_ARRAYPATH_SUPPORTED_TYPES,
      );
      if (!closest) return { nextSession: session };
      return {
        nextSession: {
          ...session,
          step: "offset-side",
          options: { ...session.options, offsetEntityId: closest.id },
          hoverPoint: null,
        },
      };
    }

    case "offset-side": {
      const entityId = session.options.offsetEntityId;
      const dist = parseFloat(session.options.offsetDistance ?? "0");
      if (!entityId || dist <= 0) return { nextSession: session };

      return {
        nextSession: {
          ...session,
          step: "offset-select-entity",
          options: {
            ...session.options,
            offsetEntityId: "",
          },
          hoverPoint: null,
        },
        commit: {
          geometryCommand: {
            type: "geometry/offset-entity",
            source: "scene",
            entityId,
            distance: dist,
            sidePoint: { x: point.x, y: point.y },
          },
          summary: t("summary.offset"),
          commandText: "OFFSET",
        },
      };
    }

    // ── STRETCH ────────────────────────────────────────
    case "stretch-select-first":
      return {
        nextSession: {
          ...session,
          step: "stretch-select-second",
          points: [point],
          hoverPoint: null,
        },
      };

    case "stretch-select-second": {
      const corner1 = session.points[0];
      const corner2 = point;
      // Find all entities that have at least one vertex inside the
      // crossing window. Store the window coordinates and entity IDs.
      const minX = Math.min(corner1.x, corner2.x);
      const maxX = Math.max(corner1.x, corner2.x);
      const minY = Math.min(corner1.y, corner2.y);
      const maxY = Math.max(corner1.y, corner2.y);

      const insideIds = existingEntities
        .filter((e) => entityIntersectsWindow(e, minX, minY, maxX, maxY))
        .map((e) => e.id);

      if (insideIds.length === 0) {
        return { nextSession: session };
      }

      return {
        nextSession: {
          ...session,
          step: "stretch-base",
          points: [corner1, corner2],
          options: {
            ...session.options,
            targetEntityIds: JSON.stringify(insideIds),
            stretchWindow: JSON.stringify({
              x1: minX,
              y1: minY,
              x2: maxX,
              y2: maxY,
            }),
          },
          hoverPoint: null,
        },
      };
    }

    case "stretch-base":
      return {
        nextSession: {
          ...session,
          step: "stretch-destination",
          points: [...session.points, point],
          hoverPoint: null,
        },
      };

    case "stretch-destination": {
      const basePoint = session.points[2]; // third point is base
      const dx = point.x - basePoint.x;
      const dy = point.y - basePoint.y;
      const entityIds = getTargetEntityIds(session);
      const stretchWindow = JSON.parse(
        session.options.stretchWindow ?? '{"x1":0,"y1":0,"x2":0,"y2":0}',
      ) as { x1: number; y1: number; x2: number; y2: number };

      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/stretch-entities",
            source: "scene",
            entityIds,
            stretchWindow,
            delta: { x: dx, y: dy },
          },
          summary: t("summary.stretch"),
          commandText: "STRETCH",
        },
      };
    }

    // ── TRIM ──────────────────────────────────────────
    case "trim-select-edges":
      // Entity selection is handled externally (click on entity to
      // add to cutting edges). Point click in empty space is ignored.
      return { nextSession: session };

    case "trim-pick-entity": {
      // User clicks on an entity to trim. Find the closest entity
      // and dispatch trim command with the stored cutting edges.
      const closest = findClosestEntity(point, existingEntities);
      if (!closest) return { nextSession: session };
      const cuttingEdgeIds = getTargetEntityIds(session);
      if (cuttingEdgeIds.length === 0) return { nextSession: session };

      return {
        nextSession: {
          ...session,
          step: "trim-pick-entity",
          hoverPoint: null,
        },
        commit: {
          geometryCommand: {
            type: "geometry/trim-entity",
            source: "scene",
            entityId: closest.id,
            cuttingEdgeIds,
            pickPoint: { x: point.x, y: point.y },
          },
          summary: t("summary.trim"),
          commandText: "TRIM",
        },
      };
    }

    // ── EXTEND ────────────────────────────────────────
    case "extend-select-edges":
      return { nextSession: session };

    case "extend-pick-entity": {
      const closest = findClosestEntity(point, existingEntities);
      if (!closest) return { nextSession: session };
      const boundaryEdgeIds = getTargetEntityIds(session);
      if (boundaryEdgeIds.length === 0) return { nextSession: session };

      return {
        nextSession: {
          ...session,
          step: "extend-pick-entity",
          hoverPoint: null,
        },
        commit: {
          geometryCommand: {
            type: "geometry/extend-entity",
            source: "scene",
            entityId: closest.id,
            boundaryEdgeIds,
            pickPoint: { x: point.x, y: point.y },
          },
          summary: t("summary.extend"),
          commandText: "EXTEND",
        },
      };
    }

    // ── FILLET ─────────────────────────────────────────
    case "fillet-radius":
      // Radius is entered via text input, not point click.
      return { nextSession: session };

    case "fillet-first-entity": {
      // Codex P2, round 17 — FILLET's reducer accepts only `Line`
      // targets; disable the point-list (LWPOLYLINE/Spline) fallback so
      // a nearby polyline vertex never outranks the intended line.
      const closest = findClosestEntity(point, existingEntities, false);
      if (!closest) return { nextSession: session };
      return {
        nextSession: {
          ...session,
          step: "fillet-second-entity",
          options: {
            ...session.options,
            filletEntityId1: closest.id,
            filletPickPoint1: JSON.stringify({ x: point.x, y: point.y }),
          },
          hoverPoint: null,
        },
      };
    }

    case "fillet-second-entity": {
      const entityId1 = session.options.filletEntityId1;
      if (!entityId1) return { nextSession: session };
      // Phase H2 — exclude the first-pick entity from the candidate set
      // when looking for the second entity. AutoCAD parity: at the
      // "Select second object" prompt, clicking on the first-picked
      // line just shows "Select second object:" again — never re-enters
      // the same entity as both sides of the fillet (which would be a
      // degenerate operation). Without this filter the script's second
      // click — even if it lands closer to the first line than to the
      // intended second — picks line-1 again and the reducer silently
      // produces no fillet arc (zero-radius default + already-meeting
      // endpoints), which manifests on live as "no fillet effect".
      const candidates = existingEntities.filter((e) => e.id !== entityId1);
      // Codex P2, round 17 — same Line-only restriction as the first pick.
      const closest = findClosestEntity(point, candidates, false);
      if (!closest) return { nextSession: session };
      const pickPoint1 = JSON.parse(
        session.options.filletPickPoint1 ?? '{"x":0,"y":0}',
      ) as { x: number; y: number };
      const radius = parseFloat(session.options.filletRadius ?? "0");

      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/fillet-entities",
            source: "scene",
            entityId1,
            entityId2: closest.id,
            radius,
            pickPoint1,
            pickPoint2: { x: point.x, y: point.y },
          },
          summary: t("summary.fillet"),
          commandText: "FILLET",
        },
      };
    }

    // ── CHAMFER ───────────────────────────────────────
    case "chamfer-distances":
      // Distances are entered via text input.
      return { nextSession: session };

    case "chamfer-first": {
      // Codex P2, round 17 — CHAMFER's reducer accepts only `Line`
      // targets; see `fillet-first-entity`'s identical comment above.
      const closest = findClosestEntity(point, existingEntities, false);
      if (!closest) return { nextSession: session };
      return {
        nextSession: {
          ...session,
          step: "chamfer-second",
          options: {
            ...session.options,
            chamferEntityId1: closest.id,
            chamferPickPoint1: JSON.stringify({ x: point.x, y: point.y }),
          },
          hoverPoint: null,
        },
      };
    }

    case "chamfer-second": {
      // Codex P2, round 17 — same Line-only restriction as the first pick.
      const closest = findClosestEntity(point, existingEntities, false);
      if (!closest) return { nextSession: session };
      const entityId1 = session.options.chamferEntityId1;
      if (!entityId1) return { nextSession: session };
      const pickPoint1 = JSON.parse(
        session.options.chamferPickPoint1 ?? '{"x":0,"y":0}',
      ) as { x: number; y: number };
      const dist1 = parseFloat(session.options.chamferDist1 ?? "0");
      const dist2 = parseFloat(session.options.chamferDist2 ?? "0");

      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/chamfer-entities",
            source: "scene",
            entityId1,
            entityId2: closest.id,
            dist1,
            dist2,
            pickPoint1,
            pickPoint2: { x: point.x, y: point.y },
          },
          summary: t("summary.chamfer"),
          commandText: "CHAMFER",
        },
      };
    }

    // ── BREAK ─────────────────────────────────────────
    case "break-select": {
      // Codex P2, round 17 — BREAK's reducer accepts only `Line` targets;
      // see `fillet-first-entity`'s identical comment above.
      const closest = findClosestEntity(point, existingEntities, false);
      if (!closest) return { nextSession: session };
      return {
        nextSession: {
          ...session,
          step: "break-first-point",
          options: {
            ...session.options,
            breakEntityId: closest.id,
          },
          hoverPoint: null,
        },
      };
    }

    case "break-first-point":
      return {
        nextSession: {
          ...session,
          step: "break-second-point",
          points: [point],
          hoverPoint: null,
        },
      };

    case "break-second-point": {
      const entityId = session.options.breakEntityId;
      if (!entityId) return { nextSession: session };
      const point1 = session.points[0];

      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/break-entity",
            source: "scene",
            entityId,
            point1: { x: point1.x, y: point1.y },
            point2: { x: point.x, y: point.y },
          },
          summary: t("summary.break"),
          commandText: "BREAK",
        },
      };
    }

    // ── JOIN ──────────────────────────────────────────
    case "join-select":
      // Entity selection accumulates via the selection flow.
      // Point clicks in empty space are ignored.
      return { nextSession: session };

    // ── EXPLODE ───────────────────────────────────────
    case "explode-select":
      // Entity selection accumulates via the selection flow.
      return { nextSession: session };

    // ── LENGTHEN ──────────────────────────────────────
    case "lengthen-select": {
      // Codex P2, round 17 — LENGTHEN's reducer accepts only `Line`
      // targets; see `fillet-first-entity`'s identical comment above.
      const closest = findClosestEntity(point, existingEntities, false);
      if (!closest) return { nextSession: session };
      return {
        nextSession: {
          ...session,
          step: "lengthen-mode",
          options: {
            ...session.options,
            lengthenEntityId: closest.id,
          },
          hoverPoint: null,
        },
      };
    }

    case "lengthen-mode":
      // Mode is entered via text input (DE/PE/TO/DY).
      return { nextSession: session };

    case "lengthen-value":
      // Value is entered via text input.
      return { nextSession: session };

    case "lengthen-pick-end": {
      const entityId = session.options.lengthenEntityId;
      const mode = (session.options.lengthenMode ?? "delta") as
        | "delta"
        | "percent"
        | "total";
      const value = parseFloat(session.options.lengthenValue ?? "0");
      if (!entityId) return { nextSession: session };

      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/lengthen-entity",
            source: "scene",
            entityId,
            mode,
            value,
            endPoint: { x: point.x, y: point.y },
          },
          summary: t("summary.lengthen"),
          commandText: "LENGTHEN",
        },
      };
    }

    // ── DYN-3 Batch E — Geometric Constraint picks ────────
    //
    // The palette-driven flow routes every click at an entity through
    // these steps until the required arity is met, then dispatches
    // `geometry/add-geometric-constraint`. Arity comes from
    // `session.options.geomKind` (see advanceGeomConstraintPick below).
    //
    // Click in empty space → stay in place (same pattern as modify
    // select steps). Click on an entity → record its id, transition
    // to the next step (or commit if arity reached).
    case "geom-constraint-pick-first":
    case "geom-constraint-pick-second":
    case "geom-constraint-pick-more":
    case "geom-constraint-pick-reference-line": {
      const closest = findClosestEntity(point, existingEntities);
      if (!closest) return { nextSession: session };
      return advanceGeomConstraintPick(session, closest.id);
    }

    // ── ARRAYRECT ─────────────────────────────────────
    case "arrayrect-select": {
      // AutoCAD parity: clicking on an entity during the "Select objects"
      // prompt picks it and adds to the selection set. The step does NOT
      // advance until Enter is pressed (handled in
      // advanceModifySessionWithText). Mirrors MOVE/COPY/MIRROR pattern.
      const picked = findClosestEntity(point, existingEntities);
      if (!picked) return { nextSession: session };
      return {
        nextSession: appendTargetEntityId(session, picked.id),
      };
    }

    case "arrayrect-base-point":
      return {
        nextSession: {
          ...session,
          step: "arrayrect-count",
          points: [point],
          hoverPoint: null,
        },
      };

    case "arrayrect-count": {
      // User picks the opposite corner — auto-calculate rows/cols
      // using AutoCAD's default cell (96x72) rounded to at least 2.
      const basePoint = session.points[0];
      const width = Math.max(
        DEFAULT_ARRAY_CELL_WIDTH,
        Math.abs(point.x - basePoint.x),
      );
      const height = Math.max(
        DEFAULT_ARRAY_CELL_HEIGHT,
        Math.abs(point.y - basePoint.y),
      );
      const signX = point.x < basePoint.x ? -1 : 1;
      const signY = point.y < basePoint.y ? -1 : 1;
      const columns =
        Number(session.options.arrayColumns) ||
        Math.max(2, Math.round(width / DEFAULT_ARRAY_CELL_WIDTH));
      const rows =
        Number(session.options.arrayRows) ||
        Math.max(2, Math.round(height / DEFAULT_ARRAY_CELL_HEIGHT));
      const columnSpacing = signX * (width / columns);
      const rowSpacing = signY * (height / rows);

      return {
        nextSession: {
          ...session,
          step: "arrayrect-spacing",
          points: [basePoint, point],
          hoverPoint: null,
          options: {
            ...session.options,
            arrayColumns: String(columns),
            arrayRows: String(rows),
            arrayColumnSpacing: String(columnSpacing),
            arrayRowSpacing: String(rowSpacing),
          },
        },
      };
    }

    case "arrayrect-spacing":
      // Spacing is normally numeric text input. A click in the scene
      // is ignored (same pattern as OFFSET distance).
      return { nextSession: session };

    // ── ARRAYPOLAR ────────────────────────────────────
    case "arraypolar-select": {
      // AutoCAD parity: clicking on an entity during the "Select objects"
      // prompt picks it; Enter advances to arraypolar-center. Mirrors
      // MOVE/COPY/MIRROR pattern.
      const picked = findClosestEntity(point, existingEntities);
      if (!picked) return { nextSession: session };
      return {
        nextSession: appendTargetEntityId(session, picked.id),
      };
    }

    case "arraypolar-center":
      // User picks the polar center point. Advance to count step
      // (which is a text-only step: user types number of items).
      return {
        nextSession: {
          ...session,
          step: "arraypolar-count",
          points: [point],
          hoverPoint: null,
        },
      };

    case "arraypolar-count":
      // Count is numeric text input only — clicks are ignored.
      return { nextSession: session };

    case "arraypolar-fill":
      // Fill angle and row options are numeric text input only.
      return { nextSession: session };

    // ── ARRAYPATH ─────────────────────────────────────
    case "arraypath-select": {
      // AutoCAD parity: clicking on an entity during the "Select objects"
      // prompt picks it; Enter advances to arraypath-path. Mirrors
      // MOVE/COPY/MIRROR pattern.
      const picked = findClosestEntity(point, existingEntities);
      if (!picked) return { nextSession: session };
      return {
        nextSession: appendTargetEntityId(session, picked.id),
      };
    }

    case "arraypath-path": {
      // User clicks on the path curve (Line / Arc / Circle / LWPOLYLINE /
      // heavy POLYLINE).
      // Restricted to the valid path types BEFORE the distance comparison
      // (Codex P2, local review, batch-5 follow-up round 3) — the OLD
      // post-hoc `PATH_TYPES.has()` check below let an INVALID-type
      // entity (e.g. a nearby Spline) win the distance race and get
      // rejected outright, even when a valid, slightly-farther candidate
      // existed and should have won instead. See
      // `OFFSET_AND_ARRAYPATH_SUPPORTED_TYPES`'s own doc comment.
      const closest = findClosestEntity(
        point,
        existingEntities,
        true,
        OFFSET_AND_ARRAYPATH_SUPPORTED_TYPES,
      );
      if (!closest) return { nextSession: session };
      // The FRAME half of ARRAYPATH's eligibility, asked AT THE PROMPT that
      // takes the path curve (@codex PR #785 round 16, P2). AutoCAD validates
      // the path curve at "Select path curve:" — an object it cannot use is
      // answered on the command line and the prompt is REPEATED — so the pick
      // is neither silently filtered out of the distance race (the defect the
      // heavy-POLYLINE finding was about: a pick ignored with no reason the
      // user can see) nor accepted only to be declined four prompts later, with
      // the method and the item count already typed.
      //
      // The verdict is `command-engine/arrayPathPlaneRefusal.ts`, the SAME
      // predicate `arrayReducers.ts` guards on and `geometryShellReducer.ts`
      // raises its audit row from, narrowed by the SAME `isPathEntity`; this
      // step adds no eligibility rule of its own. Without it the invariant
      // `arrayReducers.ts` states — the path the picker offers can never be one
      // the reducer drops — held only for the SUBTYPE half, because the type
      // set above is keyed on `EntityPreview.type` and a tilted CIRCLE is a
      // `"Circle"` like any other.
      //
      // `geometryEntities` is the typed document side; an empty one (text-only
      // callers, older tests) simply leaves the pick to the reducer's own
      // guards, exactly as before.
      const pickedGeometry =
        geometryEntities.find((entity) => entity.id === closest.id) ?? null;
      const planeDeclined =
        pickedGeometry !== null && isPathEntity(pickedGeometry)
          ? arrayPathPlaneRefusalAudit(arrayPathPlaneRefusal(pickedGeometry))
          : null;
      if (planeDeclined) {
        // Stay on `arraypath-path` — AutoCAD's re-prompt — and surface the
        // reason through the feed-only commit channel the state machine already
        // uses for validation rejections that must not touch the document.
        return {
          nextSession: session,
          commit: {
            summary: planeDeclined,
            commandText: "ARRAYPATH",
            feedOnly: true,
          },
        };
      }
      return {
        nextSession: {
          ...session,
          step: "arraypath-method",
          options: {
            ...session.options,
            arrayPathEntityId: closest.id,
          },
          hoverPoint: null,
        },
      };
    }

    case "arraypath-method":
    case "arraypath-params":
      // Both steps are text-only.
      return { nextSession: session };

    // ── HATCHEDIT ─────────────────────────────────────
    // User clicks a placed HATCH; store its id and advance to the first
    // field prompt. Filtered to Hatch entities (a click on a non-hatch is
    // a no-op — stay on the select step). Mirrors arrayedit-select's
    // pick-then-pivot shape.
    case "hatchedit-select": {
      // Hit-test the clicked point against each HATCH's REAL boundary geometry
      // (point-in-polygon on the exterior loop, else nearest-edge). HATCH
      // previews carry NO Center/Insertion coords, so the generic preview-string
      // `findClosestEntity` would skip every hatch → a dead no-op. The typed
      // `geometryEntities` carry the boundary paths the canvas pick uses;
      // `findClosestHatchPick` (drawing-model, one truth) mirrors the canvas
      // `distanceToRenderable` `case "hatch"` test. A click on no hatch keeps
      // the select step (no-op), matching arrayedit-select's stay-on-miss shape.
      //
      // P2-a pick tolerance: this handler is point-driven and does NOT receive
      // the canvas world-per-pixel scale, so it relies on `findClosestHatchPick`'s
      // inside-only default cutoff (distance 0). A click OUTSIDE every hatch
      // boundary therefore returns null → stay on select (the empty-canvas click
      // no longer advances HATCHEDIT to editing the nearest unaimed hatch). When
      // the draft pipeline later threads a world-space pick aperture, pass it as
      // the 3rd arg to widen the cutoff to the canvas `PICK_APERTURE_PX` feel.
      const hatchCandidates = geometryEntities
        .filter(
          (e): e is Extract<DrawingEntity, { type: "Hatch" }> =>
            e.type === "Hatch",
        )
        .map((e) => ({ id: e.id, boundaryPaths: e.boundaryPaths }));
      const pickedId = findClosestHatchPick(point, hatchCandidates);
      if (!pickedId) return { nextSession: session };
      return {
        nextSession: {
          ...session,
          step: "hatchedit-pattern",
          hoverPoint: null,
          options: {
            ...session.options,
            targetEntityId: pickedId,
          },
        },
      };
    }

    // ── ARRAYEDIT ─────────────────────────────────────
    case "arrayedit-select": {
      // User clicks an array member (identified via arrayGroupId on
      // the entity preview properties). Find the closest entity with
      // a non-empty "Array group" property, detect its stored
      // `arrayMode` ("rect" / "polar" / "path"), and pivot into the
      // matching edit flow with `arrayEditGroupId` and `arrayEditMode`
      // set in the session options so the final commit dispatches
      // `geometry/arrayedit` with the right mode sub-object.
      const arrayMember = findClosestArrayMember(point, existingEntities);
      if (!arrayMember) return { nextSession: session };
      const groupId = arrayMember.properties["Array group"];
      if (!groupId) return { nextSession: session };

      const detectedMode = arrayMember.properties["Array mode"];
      const mode: "rect" | "polar" | "path" =
        detectedMode === "polar" || detectedMode === "path"
          ? detectedMode
          : "rect";

      // Note: for ARRAYEDIT the geometry reducer re-scans the document
      // for entities tagged with `arraySourceOfGroup === groupId`, so
      // the UI does not need to propagate `targetEntityIds` — the
      // sources stay pinned to the original document entities.

      if (mode === "polar") {
        return {
          nextSession: {
            ...session,
            step: "arraypolar-center",
            points: [],
            hoverPoint: null,
            options: {
              ...session.options,
              arrayEditGroupId: groupId,
              arrayEditMode: "polar",
            },
          },
        };
      }

      if (mode === "path") {
        return {
          nextSession: {
            ...session,
            step: "arraypath-path",
            points: [],
            hoverPoint: null,
            options: {
              ...session.options,
              arrayEditGroupId: groupId,
              arrayEditMode: "path",
            },
          },
        };
      }

      // Rect mode (default): pivot directly to arrayrect-count which
      // is text-only (rows × cols) since spacing defaults apply.
      return {
        nextSession: {
          ...session,
          step: "arrayrect-count",
          points: [{ x: 0, y: 0 }],
          hoverPoint: null,
          options: {
            ...session.options,
            arrayEditGroupId: groupId,
            arrayEditMode: "rect",
          },
        },
      };
    }

    default:
      return { nextSession: session };
  }
}

/**
 * Read the first finite-number coordinate from `props` under any of
 * the given prefix keys (e.g. "Center " / "Start ") appended with the
 * axis ("X" / "Y"). Returns `undefined` if none of the keys yield a
 * finite number — critical for entities sitting exactly at the origin
 * where the old `parseFloat(...) || parseFloat(...) || 0` falsy chain
 * incorrectly fell through to the next key.
 */
function readCoord(
  props: Record<string, string>,
  keys: readonly string[],
  axis: "X" | "Y",
): number | undefined {
  for (const prefix of keys) {
    const raw = props[`${prefix}${axis}`];
    if (raw === undefined) continue;
    const value = parseFloat(raw);
    if (Number.isFinite(value)) return value;
  }
  return undefined;
}

/**
 * Find the entity nearest `point` that carries an "Array group"
 * property. Used by ARRAYEDIT to pick a member of an existing array.
 */
function findClosestArrayMember(
  point: DraftPoint,
  entities: EntityPreview[],
): EntityPreview | null {
  let best: EntityPreview | null = null;
  let bestDist = Infinity;
  const coordKeys = ["Center ", "Start ", "Insertion ", "Corner 1 "] as const;
  for (const entity of entities) {
    const groupId = entity.properties["Array group"];
    if (!groupId) continue;
    const cx = readCoord(entity.properties, coordKeys, "X");
    const cy = readCoord(entity.properties, coordKeys, "Y");
    if (cx === undefined || cy === undefined) continue;
    const d = Math.hypot(point.x - cx, point.y - cy);
    if (d < bestDist) {
      bestDist = d;
      best = entity;
    }
  }
  return best;
}

// ── Text input handler ──────────────────────────────────────────────

export function advanceModifySessionWithText(
  session: DraftSession,
  rawText: string,
  existingEntities: EntityPreview[],
  activeSpaceId: string,
  _defaultObjectProperties: Record<string, string>,
  // HATCHEDIT select hit-tests the composed point against a HATCH's REAL
  // boundary geometry, which only the typed entities carry. Thread it through
  // so a point-filter (FROM / M2P / .X.Y.Z) resolved from TYPED input can still
  // land a pick on a hatch — mirrors `advanceModifySessionWithPoint`. Optional
  // (defaults to []) so text-only / non-hatch callers and tests still work.
  geometryEntities: ReadonlyArray<DrawingEntity> = [],
): { nextSession: DraftSession | null; commit?: DraftCommitResult } {
  const text = rawText.trim();
  const normalizedText = text.toUpperCase();

  // OTRACK-SUBSYSTEM — `TT` interception ahead of the point-filter layer.
  const ttText = interceptTemporaryTrackPointText(session, text);
  if (ttText) return ttText;

  // Point-filter / temporary-tracking interception (FROM / M2P / .X.Y.Z —
  // ТЗ §8.6/§10.4) for MOVE/COPY/… base + destination typed input. Runs
  // before the select-keyword + scalar branches so a filter starter is
  // captured on point-accepting steps. The composed point re-enters the point
  // dispatcher WITH `geometryEntities` so a hatchedit-select pick still hits a
  // HATCH's real boundary (the point wrapper threads the same). (Codex #504 P2.)
  const filteredText = interceptPointFilterText(session, text, (s, p) =>
    advanceModifySessionWithPoint(
      s,
      p,
      existingEntities,
      activeSpaceId,
      geometryEntities,
    ),
  );
  if (filteredText) return filteredText;

  // ── MOVE/COPY/MIRROR/ROTATE/SCALE select step ─────────────────
  // AutoCAD prompts "Select objects:" until the user presses Enter.
  // Text input at this step accepts:
  //   - "L"/"Last"/"All"/"П"/"ВСЕ"  → keyword selection (resolveSelectionKeyword)
  //   - empty Enter                  → finish selection, advance to next step
  //                                    (only if at least one entity was picked)
  //
  // The step→step transitions mirror the click-pipeline for the same
  // commands: move-select → move-base-point, copy-select → copy-base-point,
  // mirror-select → mirror-first-point, rotate-select → rotate-base-point,
  // scale-select → scale-base-point.
  const SELECT_STEP_TRANSITIONS: Record<string, DraftStep> = {
    "move-select": "move-base-point",
    "copy-select": "copy-base-point",
    "mirror-select": "mirror-first-point",
    "rotate-select": "rotate-base-point",
    "scale-select": "scale-base-point",
    // ARRAYRECT / ARRAYPOLAR / ARRAYPATH all begin with the same "Select
    // objects" prompt; same Last / All / Previous keyword behaviour as
    // the modify cluster, with the next step varying by command.
    "arrayrect-select": "arrayrect-base-point",
    "arraypolar-select": "arraypolar-center",
    "arraypath-select": "arraypath-path",
  };
  if (session.step in SELECT_STEP_TRANSITIONS) {
    const nextStep = SELECT_STEP_TRANSITIONS[session.step];
    // Try keyword resolution first (Last / All / Previous).
    const keywordResolved = resolveSelectionKeyword(
      text,
      existingEntities,
      activeSpaceId,
    );
    if (keywordResolved !== null) {
      // Empty selection from "Last" with no entities yet: stay at the
      // step so the user can pick something else.
      if (keywordResolved.length === 0) {
        return { nextSession: session };
      }
      const merged = Array.from(
        new Set([...getTargetEntityIds(session), ...keywordResolved]),
      );
      return {
        nextSession: {
          ...session,
          step: nextStep,
          options: {
            ...session.options,
            targetEntityIds: JSON.stringify(merged),
          },
          hoverPoint: null,
        },
      };
    }
    // Empty Enter: advance to the next step iff at least one entity
    // is in the target set. Otherwise, stay (AutoCAD parity — Enter
    // with empty selection is a no-op at "Select objects:").
    if (text === "") {
      const ids = getTargetEntityIds(session);
      if (ids.length === 0) {
        return { nextSession: session };
      }
      return {
        nextSession: {
          ...session,
          step: nextStep,
          hoverPoint: null,
        },
      };
    }
  }

  // ── HATCHEDIT ───────────────────────────────────────────────────
  // Select-then-edit: select a placed HATCH (typed id OR click), then prompt
  // pattern / scale / angle. Each prompt accepts an empty Enter to KEEP the
  // current value (skip that field). The final step commits `geometry/hatchedit`
  // carrying only the fields the user supplied (the reducer treats ">=1 field"
  // as required and an empty edit as a no-op). Mirrors the MLEADEREDIT
  // select-then-options shape (advanceDispatch.ts) routed through this modify
  // state machine.
  if (session.step === "hatchedit-select") {
    // Empty Enter with no entity = stay (AutoCAD parity: must pick an object).
    if (text === "") return { nextSession: session };
    const target = existingEntities.find((e) => e.id === text);
    if (!target || target.type !== "Hatch") {
      // Unknown id or wrong type: stay on the select step.
      return { nextSession: session };
    }
    return {
      nextSession: {
        ...session,
        step: "hatchedit-pattern",
        hoverPoint: null,
        options: { ...session.options, targetEntityId: target.id },
      },
    };
  }
  if (session.step === "hatchedit-pattern") {
    // Empty Enter keeps the existing pattern (field omitted from the commit).
    const options = { ...session.options };
    if (text !== "") options.hatcheditPattern = text;
    return {
      nextSession: { ...session, step: "hatchedit-scale", options },
    };
  }
  if (session.step === "hatchedit-scale") {
    const options = { ...session.options };
    if (text !== "") {
      const scale = parseFloat(text);
      // Invalid / non-positive scale: re-prompt (AutoCAD rejects scale<=0).
      if (!Number.isFinite(scale) || scale <= 0) {
        return { nextSession: session };
      }
      options.hatcheditScale = String(scale);
    }
    return {
      nextSession: { ...session, step: "hatchedit-angle", options },
    };
  }
  if (session.step === "hatchedit-angle") {
    const targetEntityId = session.options.targetEntityId;
    if (!targetEntityId) return { nextSession: null };
    let angle: number | undefined;
    if (text !== "") {
      const parsed = parseFloat(text);
      if (!Number.isFinite(parsed)) {
        return { nextSession: session };
      }
      angle = parsed;
    }
    const patternName = session.options.hatcheditPattern;
    const scaleRaw = session.options.hatcheditScale;
    const patternScale =
      scaleRaw !== undefined ? Number(scaleRaw) : undefined;
    // Build the geometry/hatchedit payload carrying ONLY the supplied fields.
    // An entirely empty edit (no pattern, no scale, no angle) still commits an
    // entityId-only payload — the reducer no-ops it (">=1 field" guard), which
    // matches "press Enter through all prompts = no change".
    const geometryCommand = {
      type: "geometry/hatchedit" as const,
      source: "scene" as const,
      entityId: targetEntityId,
      ...(patternName !== undefined ? { patternName } : {}),
      ...(patternScale !== undefined && Number.isFinite(patternScale)
        ? { patternScale }
        : {}),
      ...(angle !== undefined ? { patternAngle: angle } : {}),
    };
    return {
      nextSession: null,
      commit: {
        geometryCommand,
        summary: t("summary.hatchedit_modified"),
        commandText: "HATCHEDIT",
      },
    };
  }

  // ── COPY keywords ──────────────────────────────────────────────
  //   "D" / "Displacement" on copy-base-point: switch to
  //      displacement-vector mode — next click treats origin→click as
  //      the displacement and commits a single copy.
  //   "E" / "Exit" on copy-base-point: abort COPY (same as Esc). This
  //      keeps parity with AutoCAD, which exits without a copy when
  //      the user types "E" before picking the base point.
  //   "E" / "Exit" on copy-destination: end the loop (same as Enter).
  //   "U" / "Undo" on copy-destination: undo the last committed copy
  //      via the shell/undo mechanism. Not yet wired because the shell
  //      has no geometry-undo stack — tracked in the TODO below.
  if (session.step === "copy-base-point" && text !== "") {
    if (normalizedText === "D" || normalizedText === "DISPLACEMENT") {
      return {
        nextSession: {
          ...session,
          options: {
            ...session.options,
            copyDisplacementMode: "true",
          },
        },
      };
    }
    if (normalizedText === "E" || normalizedText === "EXIT") {
      return { nextSession: null };
    }
  }

  if (session.step === "copy-destination" && text !== "") {
    if (normalizedText === "E" || normalizedText === "EXIT") {
      return { nextSession: null };
    }
    // TODO(copy-undo) — AutoCAD's COPY [Undo] pops the last committed
    // copy. We currently have no per-session undo stack in the shell
    // reducer (undo is a document-level facility used for full
    // command-undo only). When the draft session gets a proper undo
    // buffer, wire "U"/"Undo" here to dispatch shell/undo and
    // decrement `copyCommittedCount`.
    if (normalizedText === "U" || normalizedText === "UNDO") {
      return { nextSession: session };
    }
  }

  // COPY: empty Enter during copy-destination exits the loop
  if (session.step === "copy-destination" && text === "") {
    return { nextSession: null };
  }

  // COPY: empty Enter during copy-base-point is a no-op — AutoCAD
  // requires either a point or the Displacement keyword. Without this
  // early-return the Enter would fall through to the generic parser
  // below and trigger a parse failure toast.
  if (session.step === "copy-base-point" && text === "") {
    return { nextSession: session };
  }

  // MOVE: empty Enter during move-destination is no-op (user must pick)
  if (session.step === "move-destination" && text === "") {
    return { nextSession: session };
  }

  // MIRROR: empty Enter during mirror-second-point is no-op
  if (session.step === "mirror-second-point" && text === "") {
    return { nextSession: session };
  }

  // Phase C.5 — ROTATE Reference sub-flow keyword: `R`/`Reference`/
  // `Спр`/`Ссылка` at the angle step switches into the reference flow.
  // Two reference-vector clicks define a baseline angle, then the user
  // types a target absolute angle and the effective rotation is
  // computed as (newAngle - referenceAngle).
  if (session.step === "rotate-angle") {
    const upper = text.toUpperCase();
    if (
      upper === "R" ||
      upper === "REFERENCE" ||
      upper === "СПР" ||
      upper === "ССЫЛКА"
    ) {
      return {
        nextSession: {
          ...session,
          step: "rotate-ref-start",
          hoverPoint: null,
        },
      };
    }
  }

  // Phase C.5 — ROTATE Reference final new-angle text input. Subtract
  // the reference angle (captured at rotate-ref-end) from the typed
  // value to get the effective rotation.
  if (session.step === "rotate-ref-new-angle" && text !== "") {
    const newAngle = extractScalarFromText(text);
    if (newAngle !== null && Number.isFinite(newAngle)) {
      const refAngleDeg = parseFloat(session.options.rotateRefAngle ?? "0");
      const effectiveAngle = newAngle - refAngleDeg;
      const baseRef = session.points[0];
      const entityIdsRef = getTargetEntityIds(session);
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/rotate-entities",
            source: "scene",
            entityIds: entityIdsRef,
            basePoint: { x: baseRef.x, y: baseRef.y },
            angleDeg: effectiveAngle,
          },
          summary: t("summary.rotate"),
          commandText: "ROTATE",
        },
      };
    }
    return { nextSession: session };
  }

  // ROTATE: type angle in degrees
  if (session.step === "rotate-angle" && text !== "") {
    // FIX-2 bug 2: accept polar tokens (`@45<angle`) as well — the
    // dim-input field for rotation may produce polar form depending on
    // configuration; extract the magnitude for backwards compatibility.
    const angle = extractScalarFromText(text);
    if (angle !== null && Number.isFinite(angle)) {
      const basePoint = session.points[0];
      const entityIds = getTargetEntityIds(session);
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/rotate-entities",
            source: "scene",
            entityIds,
            basePoint: { x: basePoint.x, y: basePoint.y },
            angleDeg: angle,
          },
          summary: t("summary.rotate"),
          commandText: "ROTATE",
        },
      };
    }
    return { nextSession: session };
  }

  // SCALE: type scale factor
  if (session.step === "scale-factor" && text !== "") {
    // FIX-2 bug 2: accept polar tokens (`@2<angle`) — the dim-input
    // `len()` field produces polar form.
    const factor = extractScalarFromText(text);
    if (factor !== null && Number.isFinite(factor) && factor !== 0) {
      const basePoint = session.points[0];
      const entityIds = getTargetEntityIds(session);
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/scale-entities",
            source: "scene",
            entityIds,
            basePoint: { x: basePoint.x, y: basePoint.y },
            factor,
          },
          summary: t("summary.scale"),
          commandText: "SCALE",
        },
      };
    }
    return { nextSession: session };
  }

  // OFFSET: type distance
  if (session.step === "offset-distance" && text !== "") {
    // FIX-2: accept polar tokens (`@10<angle`) — the dim-input `len()`
    // field produces polar form even though only the magnitude matters.
    const dist = extractScalarFromText(text);
    if (dist !== null && Number.isFinite(dist) && dist > 0) {
      return {
        nextSession: {
          ...session,
          step: "offset-select-entity",
          options: { ...session.options, offsetDistance: String(dist) },
          hoverPoint: null,
        },
      };
    }
    return { nextSession: session };
  }

  // OFFSET: Enter during offset-select-entity exits the loop
  if (session.step === "offset-select-entity" && text === "") {
    return { nextSession: null };
  }

  // ── TRIM: Enter during trim-select-edges transitions to trim-pick-entity
  if (session.step === "trim-select-edges" && text === "") {
    const edgeIds = getTargetEntityIds(session);
    if (edgeIds.length === 0) return { nextSession: session };
    return {
      nextSession: {
        ...session,
        step: "trim-pick-entity",
        hoverPoint: null,
      },
    };
  }

  // TRIM: Enter during trim-pick-entity exits
  if (session.step === "trim-pick-entity" && text === "") {
    return { nextSession: null };
  }

  // ── EXTEND: Enter during extend-select-edges transitions to extend-pick-entity
  if (session.step === "extend-select-edges" && text === "") {
    const edgeIds = getTargetEntityIds(session);
    if (edgeIds.length === 0) return { nextSession: session };
    return {
      nextSession: {
        ...session,
        step: "extend-pick-entity",
        hoverPoint: null,
      },
    };
  }

  // EXTEND: Enter during extend-pick-entity exits
  if (session.step === "extend-pick-entity" && text === "") {
    return { nextSession: null };
  }

  // ── FIX-5 Cluster C — FILLET first-entity sub-flow keywords.
  // AutoCAD: at the "Select first object or [Undo/Polyline/Radius/
  // Trim/Multiple]" prompt, typing "R" enters radius input mode.
  // We currently only implement "R"/"Radius"/"Р" (others fall through
  // and stay at the same step — UI feedback for unsupported keywords
  // is not yet wired).
  if (session.step === "fillet-first-entity" && text !== "") {
    if (
      normalizedText === "R" ||
      normalizedText === "RADIUS" ||
      normalizedText === "Р"
    ) {
      return {
        nextSession: {
          ...session,
          step: "fillet-radius",
          hoverPoint: null,
        },
      };
    }
  }

  // ── FILLET: type radius
  if (session.step === "fillet-radius" && text !== "") {
    // FIX-2: accept polar tokens.
    const radius = extractScalarFromText(text);
    if (radius !== null && Number.isFinite(radius) && radius >= 0) {
      return {
        nextSession: {
          ...session,
          step: "fillet-first-entity",
          options: { ...session.options, filletRadius: String(radius) },
          hoverPoint: null,
        },
      };
    }
    return { nextSession: session };
  }

  // ── CHAMFER: type distances (format: "dist1 dist2", "dist1,dist2", or
  // dim-input cartesian "@d1,d2"). FIX-2: strip leading @ / # so the dim
  // input field's `wh()` token is parsed correctly.
  if (session.step === "chamfer-distances" && text !== "") {
    const stripped = text.replace(/^[@#]/, "");
    const parts = stripped.split(/[\s,]+/);
    const d1 = parseFloat(parts[0]);
    const d2 = parts.length > 1 ? parseFloat(parts[1]) : d1;
    if (Number.isFinite(d1) && Number.isFinite(d2) && d1 >= 0 && d2 >= 0) {
      return {
        nextSession: {
          ...session,
          step: "chamfer-first",
          options: {
            ...session.options,
            chamferDist1: String(d1),
            chamferDist2: String(d2),
          },
          hoverPoint: null,
        },
      };
    }
    return { nextSession: session };
  }

  // ── JOIN: Enter during join-select commits
  if (session.step === "join-select" && text === "") {
    const entityIds = getTargetEntityIds(session);
    if (entityIds.length < 2) return { nextSession: session };
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/join-entities",
          source: "scene",
          entityIds,
        },
        summary: t("summary.join"),
        commandText: "JOIN",
      },
    };
  }

  // ── DYN-3 Batch E: Enter during geom-constraint-pick-more commits
  // the unbounded-arity constraint with the picks collected so far.
  // Escape cancels (handled upstream via the normal escape flow).
  if (session.step === "geom-constraint-pick-more" && text === "") {
    return commitGeomConstraintFromMore(session);
  }

  // ── EXPLODE: Enter during explode-select commits
  if (session.step === "explode-select" && text === "") {
    const entityIds = getTargetEntityIds(session);
    if (entityIds.length === 0) return { nextSession: session };
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/explode-entities",
          source: "scene",
          entityIds,
        },
        summary: t("summary.explode"),
        commandText: "EXPLODE",
      },
    };
  }

  // ── LENGTHEN: mode selection (DE/PE/TO)
  if (session.step === "lengthen-mode" && text !== "") {
    const upper = text.toUpperCase();
    let mode: string | null = null;
    if (upper === "DE" || upper === "DELTA") mode = "delta";
    else if (upper === "PE" || upper === "PERCENT") mode = "percent";
    else if (upper === "TO" || upper === "TOTAL") mode = "total";
    if (mode) {
      return {
        nextSession: {
          ...session,
          step: "lengthen-value",
          options: { ...session.options, lengthenMode: mode },
          hoverPoint: null,
        },
      };
    }
    return { nextSession: session };
  }

  // ── LENGTHEN: value input
  if (session.step === "lengthen-value" && text !== "") {
    // FIX-2: accept polar tokens.
    const value = extractScalarFromText(text);
    if (value !== null && Number.isFinite(value)) {
      return {
        nextSession: {
          ...session,
          step: "lengthen-pick-end",
          options: { ...session.options, lengthenValue: String(value) },
          hoverPoint: null,
        },
      };
    }
    return { nextSession: session };
  }

  // ── ARRAYRECT: selection step — Enter finishes selection and goes
  // to base-point step (only if at least one entity selected)
  if (session.step === "arrayrect-select" && text === "") {
    const entityIds = getTargetEntityIds(session);
    if (entityIds.length === 0) return { nextSession: session };
    return {
      nextSession: {
        ...session,
        step: "arrayrect-base-point",
        hoverPoint: null,
      },
    };
  }

  // ── ARRAYRECT: count step — parse COL=n / ROW=n; Enter advances
  // to spacing with whatever is already set (or defaults 3x3).
  if (session.step === "arrayrect-count") {
    const colMatch = /^(?:C|COL|COLUMNS?)\s*=?\s*(\d+)$/iu.exec(text);
    if (colMatch) {
      return {
        nextSession: {
          ...session,
          options: {
            ...session.options,
            arrayColumns: colMatch[1],
          },
        },
      };
    }

    const rowMatch = /^(?:R|ROW|ROWS)\s*=?\s*(\d+)$/iu.exec(text);
    if (rowMatch) {
      return {
        nextSession: {
          ...session,
          options: {
            ...session.options,
            arrayRows: rowMatch[1],
          },
        },
      };
    }

    if (text === "") {
      const columns = Number(session.options.arrayColumns) || 3;
      const rows = Number(session.options.arrayRows) || 3;
      // Default spacing 10 units if the user never picked a corner.
      const columnSpacing = Number(session.options.arrayColumnSpacing) || 10;
      const rowSpacing = Number(session.options.arrayRowSpacing) || 10;
      return {
        nextSession: {
          ...session,
          step: "arrayrect-spacing",
          hoverPoint: null,
          options: {
            ...session.options,
            arrayColumns: String(columns),
            arrayRows: String(rows),
            arrayColumnSpacing: String(columnSpacing),
            arrayRowSpacing: String(rowSpacing),
          },
        },
      };
    }
  }

  // ── ARRAYPOLAR: selection step — Enter finishes selection and goes
  // to arraypolar-center step (only if at least one entity selected)
  if (session.step === "arraypolar-select" && text === "") {
    const entityIds = getTargetEntityIds(session);
    if (entityIds.length === 0) return { nextSession: session };
    return {
      nextSession: {
        ...session,
        step: "arraypolar-center",
        hoverPoint: null,
      },
    };
  }

  // ── ARRAYPOLAR: count step — numeric item count; Enter advances
  // to fill step with a default of 6 items.
  if (session.step === "arraypolar-count") {
    if (text === "") {
      const itemCount = Number(session.options.arrayItemCount) || 6;
      return {
        nextSession: {
          ...session,
          step: "arraypolar-fill",
          hoverPoint: null,
          options: {
            ...session.options,
            arrayItemCount: String(itemCount),
          },
        },
      };
    }

    const count = parseInt(text, 10);
    if (Number.isFinite(count) && count >= 2) {
      return {
        nextSession: {
          ...session,
          step: "arraypolar-fill",
          hoverPoint: null,
          options: {
            ...session.options,
            arrayItemCount: String(count),
          },
        },
      };
    }
    return { nextSession: session };
  }

  // ── ARRAYPOLAR: fill step — parse fillAngle, ROW=n, RS=n.
  // Enter commits the command with accumulated options.
  if (session.step === "arraypolar-fill") {
    // ROW=n — number of radial rings (concentric rows)
    const rowMatch = /^(?:ROW|ROWS|R)\s*=?\s*(\d+)$/iu.exec(text);
    if (rowMatch) {
      return {
        nextSession: {
          ...session,
          options: {
            ...session.options,
            arrayRowCount: rowMatch[1],
          },
        },
      };
    }

    // RS=n — radial spacing between rings
    const rsMatch =
      /^(?:RS|ROWSPACING|ROWSPC)\s*=?\s*(-?\d+(?:\.\d+)?)$/iu.exec(text);
    if (rsMatch) {
      return {
        nextSession: {
          ...session,
          options: {
            ...session.options,
            arrayRowSpacing: rsMatch[1],
          },
        },
      };
    }

    // ROT=Y/N — toggle rotate-items
    const rotMatch = /^(?:ROT|ROTATE)\s*=?\s*([YN])$/iu.exec(text);
    if (rotMatch) {
      return {
        nextSession: {
          ...session,
          options: {
            ...session.options,
            arrayRotateItems: rotMatch[1].toUpperCase() === "Y" ? "1" : "0",
          },
        },
      };
    }

    if (text === "") {
      const entityIds = getTargetEntityIds(session);
      const centerPoint = session.points[0];
      if (!centerPoint) return { nextSession: session };

      const itemCount = Number(session.options.arrayItemCount) || 6;
      const fillAngle = Number(session.options.arrayFillAngle) || 360;
      const rowCount = Number(session.options.arrayRowCount) || 1;
      const rowSpacing = Number(session.options.arrayRowSpacing) || 0;
      const rotateItems = session.options.arrayRotateItems !== "0";

      // ARRAYEDIT branch: if we entered this flow via arrayedit-select
      // (arrayEditGroupId is set), dispatch `geometry/arrayedit` to
      // re-generate the polar group rather than creating a new one.
      const editGroupId = session.options.arrayEditGroupId;
      if (editGroupId) {
        const editCopies = Math.max(0, itemCount * rowCount - 1);
        return {
          nextSession: null,
          commit: {
            geometryCommand: {
              type: "geometry/arrayedit",
              source: "scene",
              arrayGroupId: editGroupId,
              mode: "polar",
              polar: {
                centerPoint: { x: centerPoint.x, y: centerPoint.y },
                itemCount,
                fillAngle,
                rotateItems,
                rowCount,
                rowSpacing,
              },
            },
            summary: t("summary.arrayedit_modified", { n: editCopies }),
            commandText: "ARRAYEDIT",
          },
        };
      }

      if (entityIds.length === 0) return { nextSession: session };

      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/create-arraypolar",
            source: "scene",
            entityIds,
            arrayGroupId: `arr-${globalThis.crypto.randomUUID()}`,
            centerPoint: { x: centerPoint.x, y: centerPoint.y },
            itemCount,
            fillAngle,
            rotateItems,
            rowCount,
            rowSpacing,
          },
          summary: t("summary.arraypolar_created", {
            n: Math.max(0, itemCount * rowCount - 1) * entityIds.length,
          }),
          commandText: "ARRAYPOLAR",
        },
      };
    }

    // Plain numeric input — interpret as fillAngle in degrees.
    const fillAngle = parseFloat(text);
    if (Number.isFinite(fillAngle)) {
      return {
        nextSession: {
          ...session,
          options: {
            ...session.options,
            arrayFillAngle: String(fillAngle),
          },
        },
      };
    }
    return { nextSession: session };
  }

  // ── ARRAYRECT: spacing step — parse CS=n / RS=n; Enter commits.
  if (session.step === "arrayrect-spacing") {
    const csMatch =
      /^(?:CS|COLSPACING|COLSPC)\s*=?\s*(-?\d+(?:\.\d+)?)$/iu.exec(text);
    if (csMatch) {
      return {
        nextSession: {
          ...session,
          options: {
            ...session.options,
            arrayColumnSpacing: csMatch[1],
          },
        },
      };
    }

    const rsMatch =
      /^(?:RS|ROWSPACING|ROWSPC)\s*=?\s*(-?\d+(?:\.\d+)?)$/iu.exec(text);
    if (rsMatch) {
      return {
        nextSession: {
          ...session,
          options: {
            ...session.options,
            arrayRowSpacing: rsMatch[1],
          },
        },
      };
    }

    const aaMatch = /^(?:AA|AXIS|ANGLE)\s*=?\s*(-?\d+(?:\.\d+)?)$/iu.exec(text);
    if (aaMatch) {
      return {
        nextSession: {
          ...session,
          options: {
            ...session.options,
            arrayAxisAngle: aaMatch[1],
          },
        },
      };
    }

    if (text === "") {
      const columns = Number(session.options.arrayColumns) || 3;
      const rows = Number(session.options.arrayRows) || 3;
      const columnSpacing = Number(session.options.arrayColumnSpacing) || 10;
      const rowSpacing = Number(session.options.arrayRowSpacing) || 10;
      const axisAngle = Number(session.options.arrayAxisAngle) || 0;

      // ARRAYEDIT branch: if we entered this flow via arrayedit-select
      // (arrayEditGroupId is set), dispatch `geometry/arrayedit` to
      // re-generate the group rather than creating a new one.
      const editGroupId = session.options.arrayEditGroupId;
      if (editGroupId) {
        const editCopies = Math.max(0, rows * columns - 1);
        return {
          nextSession: null,
          commit: {
            geometryCommand: {
              type: "geometry/arrayedit",
              source: "scene",
              arrayGroupId: editGroupId,
              mode: "rect",
              rect: {
                rowCount: rows,
                columnCount: columns,
                rowSpacing,
                columnSpacing,
                axisAngle,
              },
            },
            summary: t("summary.arrayedit_modified", { n: editCopies }),
            commandText: "ARRAYEDIT",
          },
        };
      }

      const entityIds = getTargetEntityIds(session);
      if (entityIds.length === 0) return { nextSession: session };
      const basePoint = session.points[0];
      if (!basePoint) return { nextSession: session };

      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/create-arrayrect",
            source: "scene",
            entityIds,
            arrayGroupId: `arr-${globalThis.crypto.randomUUID()}`,
            basePoint: { x: basePoint.x, y: basePoint.y },
            rowCount: rows,
            columnCount: columns,
            rowSpacing,
            columnSpacing,
            axisAngle,
          },
          summary: t("summary.arrayrect_created", {
            n: (rows * columns - 1) * entityIds.length,
          }),
          commandText: "ARRAYRECT",
        },
      };
    }
  }

  // ── ARRAYPATH: selection step — Enter finishes selection and goes
  // to arraypath-path step (only if at least one entity selected)
  if (session.step === "arraypath-select" && text === "") {
    const entityIds = getTargetEntityIds(session);
    if (entityIds.length === 0) return { nextSession: session };
    return {
      nextSession: {
        ...session,
        step: "arraypath-path",
        hoverPoint: null,
      },
    };
  }

  // ── ARRAYPATH: path-pick step is point-only; Enter is no-op.
  if (session.step === "arraypath-path" && text === "") {
    return { nextSession: session };
  }

  // ── ARRAYPATH: method step — parse D/DIVIDE/M/MEASURE; default Divide.
  if (session.step === "arraypath-method") {
    if (text === "") {
      return {
        nextSession: {
          ...session,
          step: "arraypath-params",
          hoverPoint: null,
          options: {
            ...session.options,
            arrayPathMethod: session.options.arrayPathMethod ?? "divide",
          },
        },
      };
    }
    const upper = text.toUpperCase();
    let method: "divide" | "measure" | null = null;
    if (upper === "D" || upper === "DIVIDE") method = "divide";
    else if (upper === "M" || upper === "MEASURE") method = "measure";
    if (method) {
      return {
        nextSession: {
          ...session,
          step: "arraypath-params",
          hoverPoint: null,
          options: {
            ...session.options,
            arrayPathMethod: method,
          },
        },
      };
    }
    return { nextSession: session };
  }

  // ── ARRAYPATH: params step — parse count (divide) or spacing
  // (measure). Enter with empty text applies the default: 6 copies for
  // divide or half the path length for measure.
  if (session.step === "arraypath-params") {
    const entityIds = getTargetEntityIds(session);
    const pathEntityId = session.options.arrayPathEntityId;
    const method = (session.options.arrayPathMethod ?? "divide") as
      | "divide"
      | "measure";
    const editGroupId = session.options.arrayEditGroupId;

    // In ARRAYEDIT mode we don't need entityIds (the reducer re-reads
    // sources from the existing group); in create mode we do.
    if (!pathEntityId) return { nextSession: session };
    if (!editGroupId && entityIds.length === 0) {
      return { nextSession: session };
    }

    const commit = (
      itemCount?: number,
      itemSpacing?: number,
    ): { nextSession: DraftSession | null; commit?: DraftCommitResult } => {
      // Compute expected copy count for summary text. Exact count is
      // determined by the reducer; here we supply a reasonable estimate:
      // for divide → (itemCount - 1) * entityIds.length; for measure we
      // don't know precisely without the path length, so fall back to 1
      // placeholder and let the reducer produce the real count later
      // (summary is immediate; it's a UI-only display).
      const copies =
        method === "divide" && itemCount && itemCount >= 2
          ? (itemCount - 1) * Math.max(1, entityIds.length)
          : Math.max(1, entityIds.length);

      // ARRAYEDIT branch: regenerate the existing path array group.
      if (editGroupId) {
        return {
          nextSession: null,
          commit: {
            geometryCommand: {
              type: "geometry/arrayedit",
              source: "scene",
              arrayGroupId: editGroupId,
              mode: "path",
              path: {
                pathEntityId,
                method,
                itemCount,
                itemSpacing,
                alignItems: true,
              },
            },
            summary: t("summary.arrayedit_modified", { n: copies }),
            commandText: "ARRAYEDIT",
          },
        };
      }

      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/create-arraypath",
            source: "scene",
            entityIds,
            arrayGroupId: `arr-${globalThis.crypto.randomUUID()}`,
            pathEntityId,
            method,
            itemCount,
            itemSpacing,
            alignItems: true,
          },
          summary: t("summary.arraypath_created", { n: copies }),
          commandText: "ARRAYPATH",
        },
      };
    };

    if (text === "") {
      // Default: 6 for divide, half of assumed path length ~ 50 units
      // for measure. The reducer will validate spacing > 0, so we
      // supply a modest default (50) for measure and rely on the user
      // to type a better value if the path is very small.
      if (method === "divide") return commit(6, undefined);
      return commit(undefined, 50);
    }

    const value = parseFloat(text);
    if (Number.isFinite(value) && value > 0) {
      if (method === "divide") {
        const count = Math.round(value);
        if (count >= 2) return commit(count, undefined);
        return { nextSession: session };
      }
      return commit(undefined, value);
    }
    return { nextSession: session };
  }

  // Try parsing coordinate input for point-accepting steps.
  // parsePointToken returns a ParseResult<CoordinatePoint> with shape
  // { ok: true, value: { x, y, z? } } or { ok: false, code, ... }.
  // We pass referencePoint/directionPoint in the options so the parser
  // resolves @rel, @polar and direct-distance tokens itself — the old
  // fall-back code that indexed parsed.x / parsed.relative directly was
  // broken: those fields are nested under `.value`, so every text point
  // input produced { x: undefined, y: undefined } and crashed the
  // downstream render with `undefined.toFixed is not a function`.
  if (text !== "") {
    const referencePoint = referencePointForDraftTextInput(session);
    const directionPoint = session.hoverPoint ?? null;
    const parsedResult = parsePointToken(text, {
      referencePoint: referencePoint ? { ...referencePoint, z: 0 } : null,
      directionPoint:
        directionPoint && referencePoint ? { ...directionPoint, z: 0 } : null,
    });

    if (parsedResult.ok) {
      const absolutePoint: DraftPoint = {
        x: parsedResult.value.x,
        y: parsedResult.value.y,
      };

      return advanceModifySessionWithPoint(
        session,
        absolutePoint,
        existingEntities,
        activeSpaceId,
      );
    }
  }

  return { nextSession: session };
}

// ── Helpers ─────────────────────────────────────────────────────────

/**
 * Extract the target entity IDs from the session options. These are set
 * by the caller (useDraftSession) when a selection exists at command start.
 */
function getTargetEntityIds(session: DraftSession): string[] {
  const raw = session.options.targetEntityIds;
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.filter((id): id is string => typeof id === "string");
    }
  } catch {
    // If parsing fails, treat as a single ID
  }
  return raw ? [raw] : [];
}

/**
 * Add an entity id to the session's `targetEntityIds` option, preserving
 * existing entries and avoiding duplicates. Used by select-step click
 * handlers (MOVE/COPY/MIRROR/ROTATE/SCALE) to accumulate the selection
 * set as the user picks objects one by one.
 */
function appendTargetEntityId(
  session: DraftSession,
  entityId: string,
): DraftSession {
  const existing = getTargetEntityIds(session);
  if (existing.includes(entityId)) {
    return session;
  }
  const next = [...existing, entityId];
  return {
    ...session,
    options: {
      ...session.options,
      targetEntityIds: JSON.stringify(next),
    },
  };
}

/**
 * Resolve the AutoCAD-style "Last" / "All" / "Previous" selection
 * keywords to a list of entity ids. Returns null when the keyword is
 * not recognised so the caller can fall through to other handlers.
 *
 * AutoCAD parity:
 *   - "L" / "Last"     → most recently created entity in the active space
 *   - "All"            → every entity in the active space
 *   - "P" / "Previous" → previous selection set (currently a no-op since
 *                        the shell does not yet keep a per-command
 *                        selection-history stack)
 */
function resolveSelectionKeyword(
  text: string,
  existingEntities: EntityPreview[],
  activeSpaceId: string,
): string[] | null {
  const upper = text.trim().toUpperCase();
  if (upper === "") return null;

  const inSpace = existingEntities.filter(
    (entity) => entity.spaceId === activeSpaceId,
  );

  if (upper === "L" || upper === "LAST" || upper === "П") {
    const last = inSpace.at(-1);
    return last ? [last.id] : [];
  }
  if (upper === "ALL" || upper === "ВСЕ") {
    return inSpace.map((entity) => entity.id);
  }
  return null;
}

/**
 * Find the entity closest to the given point by checking the entity's
 * summary for coordinate data. This is a simplified hit-test using the
 * entity preview properties (Center X/Y, Start X/Y, Insertion X/Y).
 *
 * `allowPointListEntities` (Codex P2, round 17) — see
 * `resolveEntityPreviewCoordinate`'s own doc comment. Pass `false` from a
 * pick step whose reducer accepts only `Line` targets (FILLET, CHAMFER,
 * BREAK, LENGTHEN) so a nearby LWPOLYLINE/Spline vertex never outranks
 * the intended line and causes the command to silently no-op.
 *
 * `restrictToTypes` (Codex P2, local review, batch-5 follow-up round 3) —
 * a coarser problem than `allowPointListEntities` alone can solve: even
 * with point-list candidates enabled, a caller whose reducer supports only
 * a SUBSET of entity types (e.g. OFFSET: Line/Circle/Arc/LWPOLYLINE, never
 * Spline) still let an unsupported-type entity (a nearby Spline) win the
 * distance comparison, advance the session, and then silently no-op once
 * the reducer's own type switch fell through its `default` case — the
 * SAME "outranks the intended target, command does nothing" failure class
 * `allowPointListEntities=false` already fixes for Line-only reducers,
 * just for a wider (but still not universal) supported-type set. Filters
 * candidates BEFORE the distance comparison — matching `arraypath-path`'s
 * own POST-hoc `PATH_TYPES.has()` check, which had the identical bug: an
 * invalid-type entity could already win the distance race and get
 * rejected outright, even when a valid, slightly-farther candidate
 * existed and should have been picked instead.
 */
function findClosestEntity(
  point: DraftPoint,
  entities: EntityPreview[],
  allowPointListEntities = true,
  restrictToTypes?: ReadonlySet<string>,
): EntityPreview | null {
  let best: EntityPreview | null = null;
  let bestDist = Infinity;

  for (const entity of entities) {
    if (restrictToTypes && !restrictToTypes.has(entity.type)) continue;
    const coord = resolveEntityPreviewCoordinate(entity.properties, point, allowPointListEntities);
    if (coord) {
      const d = Math.hypot(point.x - coord.x, point.y - coord.y);
      if (d < bestDist) {
        bestDist = d;
        best = entity;
      }
    }
  }

  return best;
}

/**
 * Entity types OFFSET's own reducer (`geometryReducerEdit.ts`'s
 * `geometry/offset-entity` case) and `arraypath-path`'s selection step
 * both support — the SAME set for both (Codex P2, local review, batch-5
 * follow-up round 3): OFFSET's reducer switch has cases for exactly these
 * types (`default:` silently no-ops for anything else, Spline included),
 * and `arraypath-path`'s own path curve must already be one of these to
 * become a valid array path. Shared as ONE constant so the two call sites
 * can never drift out of sync with each other or with the reducer's own
 * actual supported-type switch.
 *
 * ## Why `"Polyline"` joined it WHOLE — @codex PR #785 round 14
 *
 * The heavy `POLYLINE` is ONE entity type covering four AutoCAD objects
 * (group 70: 2D polyline, 3D polyline, polygon mesh, poly-face mesh), and
 * `EntityPreview.type` is `"Polyline"` for all four — a set keyed on the
 * type string cannot tell them apart, and it must not try. The two findings
 * this closes were BOTH "the picker filters the entity out with no reason
 * the user can see": narrowing the admitted subtype HERE would repeat that
 * defect one level down, since a silently-skipped pick is indistinguishable
 * from a missed click.
 *
 * So every heavy polyline is PICKABLE, and the subtype verdict is taken
 * where it can be REPORTED: `command-engine/heavyPolylineCurveRefusal.ts` is
 * the one eligibility truth, read by OFFSET's reducer arm, by
 * `arrayReducers.ts`'s `isPathEntity`, and by `geometryShellReducer.ts`,
 * which turns a declined pick into an RU+EN warning-tone audit row naming
 * the subtype and the reason. The invariant this constant exists for is
 * therefore intact and stronger: every type in the set has a real arm in
 * BOTH reducers, and every input those arms decline is declined VISIBLY.
 *
 * ## The same holds for the FRAME — @codex PR #785 round 16
 *
 * A tilted CIRCLE is a `"Circle"` and a climbing LINE is a `"Line"`, so this
 * set cannot see the OTHER half of ARRAYPATH's eligibility either, and must
 * not try: `EntityPreview` carries no group 210 and no group 30. The frame
 * verdict is taken in the `arraypath-path` step itself, from the typed
 * document entity and `command-engine/arrayPathPlaneRefusal.ts` — the pick is
 * ADMITTED to the distance race and then answered on the command line with the
 * same text the audit ring uses, the prompt repeating as AutoCAD's does. A
 * membership test here would have been the silent filter all over again.
 */
const OFFSET_AND_ARRAYPATH_SUPPORTED_TYPES: ReadonlySet<string> = new Set([
  "Line",
  "Circle",
  "Arc",
  "LWPOLYLINE",
  "Polyline",
]);

/**
 * Check whether an entity has at least one recognisable coordinate
 * inside the given window rectangle. Used by STRETCH to identify
 * which entities fall within the crossing window.
 */
function entityIntersectsWindow(
  entity: EntityPreview,
  minX: number,
  minY: number,
  maxX: number,
  maxY: number,
): boolean {
  const props = entity.properties;
  // Collect all recognisable coordinate pairs from the properties
  const coordPairs: Array<{ x: number; y: number }> = [];

  const coordKeys = [
    ["Start X", "Start Y"],
    ["End X", "End Y"],
    ["Center X", "Center Y"],
    ["Insertion X", "Insertion Y"],
    ["Corner 1 X", "Corner 1 Y"],
    ["Corner 2 X", "Corner 2 Y"],
    ["Extension 1 X", "Extension 1 Y"],
    ["Extension 2 X", "Extension 2 Y"],
    ["Dimension line X", "Dimension line Y"],
    ["Arrow X", "Arrow Y"],
    ["Landing X", "Landing Y"],
  ];

  for (const [xKey, yKey] of coordKeys) {
    const x = parseFloat(props[xKey] ?? "");
    const y = parseFloat(props[yKey] ?? "");
    if (Number.isFinite(x) && Number.isFinite(y)) {
      coordPairs.push({ x, y });
    }
  }

  // Check if any coordinate falls inside the window
  return coordPairs.some(
    (p) => p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY,
  );
}

/**
 * Extract a scalar numeric value from a draft-text input.
 *
 * AutoCAD-parity background: Dimension Input fields configured with
 * `kind: "length"` (e.g. `rotate-angle`, `scale-factor`, `offset-distance`,
 * `fillet-radius`, `lengthen-value`) build their commit token via
 * `buildDimCommitToken` as a polar coordinate `@<value><angle>` — even when
 * the underlying step expects a plain number. State-machine handlers that
 * read scalar values via `parseFloat(text)` then receive `NaN` and refuse
 * to advance, freezing the session.
 *
 * Recognises:
 *   - raw number  → `Number(text)`
 *   - polar token → `@<value><angle>` → returns `value`
 *   - cartesian   → `@<dx>,<dy>` → returns `Math.hypot(dx, dy)` (length of vector)
 *   - empty       → `null`
 *
 * Mirrors `extractScalarFromDraftText` in draftStateMachine.ts. The two
 * helpers are duplicated rather than shared to avoid a circular import
 * between the modify and main state machines (they're sibling modules
 * that both re-export from draftingHelpers).
 */
function extractScalarFromText(rawText: string): number | null {
  const text = rawText.trim();
  if (text === "") return null;

  const ltIdx = text.indexOf("<");
  if (ltIdx >= 0) {
    const head = text.slice(0, ltIdx).replace(/^[@#]/, "");
    const value = Number(head);
    if (Number.isFinite(value)) return value;
    return null;
  }

  const commaIdx = text.indexOf(",");
  if (commaIdx >= 0) {
    const head = text.slice(0, commaIdx).replace(/^[@#]/, "");
    const tail = text.slice(commaIdx + 1);
    const dx = Number(head);
    const dy = Number(tail);
    if (Number.isFinite(dx) && Number.isFinite(dy)) {
      return Math.hypot(dx, dy);
    }
    return null;
  }

  const stripped = text.replace(/^[@#]/, "");
  const value = Number(stripped);
  return Number.isFinite(value) ? value : null;
}
